<template>
  <div
    class="balance-card bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 shadow-lg"
  >
    <!-- Loading State -->
    <div v-if="loading" class="space-y-4">
      <div class="animate-pulse">
        <div class="h-4 bg-white/20 rounded w-1/3 mb-2"></div>
        <div class="h-8 bg-white/20 rounded w-2/3"></div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="space-y-3">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-red-200" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span class="text-sm text-red-100 font-medium">Erreur de chargement</span>
      </div>
      <button
        type="button"
        class="text-sm text-white underline hover:text-white/80"
        @click="handleRetry"
      >
        Réessayer
      </button>
    </div>

    <!-- Normal State -->
    <div v-else class="space-y-4">
      <!-- Label -->
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-white/80" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
          <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
        </svg>
        <span class="text-sm text-white/80 font-medium">Solde disponible</span>
      </div>

      <!-- Balance Amount -->
      <div class="space-y-1">
        <div class="flex items-baseline gap-1">
          <span class="text-4xl font-bold text-white">{{ formatAmount(balance) }}</span>
          <span class="text-lg text-white/80 font-medium">{{ currency }}</span>
        </div>
        <div class="text-xs text-white/60">
          Dernière mise à jour: {{ lastUpdateText }}
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="flex gap-2 pt-2">
        <button
          type="button"
          class="flex-1 bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-xl text-sm font-medium transition-colors"
          @click="handleDeposit"
        >
          + Déposer
        </button>
        <button
          type="button"
          class="flex-1 bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-xl text-sm font-medium transition-colors"
          :disabled="balance <= 0"
          @click="handleWithdraw"
        >
          - Retirer
        </button>
      </div>
    </div>

    <!-- Decorative Elements -->
    <div class="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4"></div>
    <div class="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4"></div>
  </div>
</template>

<script>
export default {
  name: 'BalanceCard',

  props: {
    balance: {
      type: Number,
      default: 0
    },
    currency: {
      type: String,
      default: 'XOF'
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      lastUpdate: new Date()
    };
  },

  computed: {
    lastUpdateText() {
      const now = new Date();
      const diff = Math.floor((now - this.lastUpdate) / 1000);

      if (diff < 60) return "À l'instant";
      if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
      return `Il y a ${Math.floor(diff / 86400)}j`;
    }
  },

  methods: {
    formatAmount(amount) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    handleRetry() {
      this.$bus.emit('WALLET.RETRY_BALANCE', {
        timestamp: Date.now()
      });
    },

    handleDeposit() {
      this.$bus.emit('WALLET.OPEN_DEPOSIT_MODAL', {
        timestamp: Date.now()
      });
    },

    handleWithdraw() {
      if (this.balance > 0) {
        this.$bus.emit('WALLET.OPEN_WITHDRAWAL_MODAL', {
          balance: this.balance,
          timestamp: Date.now()
        });
      }
    }
  }
};
</script>

<style scoped>
.balance-card {
  position: relative;
  overflow: hidden;
}
</style>
